import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TopBar } from "@/components/TopBar";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowLeft, Tv, Timer, TrendingUp, MinusCircle, Sparkles } from "lucide-react";

function fmt(mins) {
  const m = Math.max(0, mins || 0);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

export default function ScreenTimePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isParent = user?.role === "Parent";
  const [students, setStudents] = useState([]);
  const [sid, setSid] = useState("");
  const [data, setData] = useState(null);
  const [redeem, setRedeem] = useState("");
  const [note, setNote] = useState("");

  const targetId = isParent ? sid : user?.id;
  const accent = isParent ? "#E07A5F" : "#81B29A";

  const load = useCallback(() => {
    if (!targetId) return;
    api.get(`/screen-time/${targetId}`).then((r) => setData(r.data));
  }, [targetId]);

  useEffect(() => {
    if (isParent) api.get("/students").then((r) => { setStudents(r.data); if (r.data[0]) setSid(r.data[0].id); });
  }, [isParent]);
  useEffect(() => { load(); }, [load]);

  const doRedeem = async () => {
    const mins = Number(redeem);
    if (!mins || mins < 1) return toast.error("Enter minutes to log.");
    try {
      await api.post(`/screen-time/${targetId}/redeem`, { minutes: mins, note });
      setRedeem(""); setNote(""); toast.success(`Logged ${mins} min used.`); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const maxWeek = data ? Math.max(1, ...data.weekly.map((w) => w.minutes)) : 1;

  return (
    <div>
      <TopBar subtitle="Screen Time" />
      <main className="max-w-3xl mx-auto px-6 md:px-8 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <button onClick={() => navigate(isParent ? "/parent/chores" : "/student/chores")} className="flex items-center gap-2 text-sm text-[#6B7280] hover:text-[#3D405B]"><ArrowLeft size={16} /> Back to Chores</button>
          {isParent && (
            <select data-testid="screentime-student" value={sid} onChange={(e) => setSid(e.target.value)}
              className="rounded-full border border-[#EAE6DF] bg-white px-5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#E07A5F]">
              {students.map((s) => <option key={s.id} value={s.id}>{s.first_name}</option>)}
            </select>
          )}
        </div>

        {data && (
          <>
            <div className="relative grain rounded-3xl overflow-hidden p-8 text-white mb-8" style={{ background: "#3D405B" }}>
              <div className="flex items-center gap-2 text-white/80"><Tv size={16} /><span className="text-xs font-semibold uppercase tracking-[0.2em]">Screen-Time Balance</span></div>
              <p className="stat text-6xl font-bold mt-2" data-testid="screentime-balance">{fmt(data.balance)}</p>
              <p className="text-white/80 mt-1">available to use</p>
              <div className="flex flex-wrap items-center gap-6 mt-5 text-sm">
                <span className="flex items-center gap-2"><Timer size={18} className="text-[#F4A261]" /> Earned this week: <b>{fmt(data.earned_week)}</b></span>
                <span className="flex items-center gap-2"><TrendingUp size={18} className="text-[#81B29A]" /> Earned all-time: <b>{fmt(data.earned_total)}</b></span>
                <span className="flex items-center gap-2"><MinusCircle size={18} className="text-white/70" /> Used: <b>{fmt(data.redeemed_total)}</b></span>
              </div>
            </div>

            {!isParent && (
              <p className="mb-8 text-sm text-[#6B7280] flex items-center gap-2"><Sparkles size={15} className="text-[#F4A261]" /> Finish chores to earn more minutes! Ask a parent to log time when you use it.</p>
            )}

            <h2 className="font-head text-lg font-semibold text-[#3D405B] mb-4">Minutes earned each week</h2>
            <div className="rounded-2xl bg-white border border-[#EAE6DF] p-6 mb-8">
              <div className="flex items-end justify-between gap-3 h-40">
                {data.weekly.map((w, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center justify-end h-full gap-2">
                    <span className="stat text-xs text-[#3D405B]">{w.minutes || ""}</span>
                    <motion.div initial={{ height: 0 }} animate={{ height: `${(w.minutes / maxWeek) * 100}%` }} transition={{ delay: i * 0.06 }}
                      className="w-full rounded-t-lg" style={{ background: accent, minHeight: w.minutes ? 6 : 0 }} />
                    <span className="text-[10px] text-[#6B7280] text-center">{w.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {isParent && (
              <div className="rounded-2xl bg-white border border-[#EAE6DF] p-6 mb-8">
                <p className="font-head font-semibold text-[#3D405B] mb-3">Log used screen time</p>
                <p className="text-sm text-[#6B7280] mb-3">When your child spends their earned minutes on phone or TV, log it here to update their balance.</p>
                <div className="flex flex-wrap items-center gap-3">
                  <input data-testid="redeem-minutes" type="number" min={1} value={redeem} onChange={(e) => setRedeem(e.target.value)} placeholder="Minutes"
                    className="w-28 rounded-xl border border-[#EAE6DF] px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#E07A5F]" />
                  <input data-testid="redeem-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional, e.g. TV after dinner)"
                    className="flex-1 min-w-[180px] rounded-xl border border-[#EAE6DF] px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#E07A5F]" />
                  <button data-testid="redeem-btn" onClick={doRedeem} className="flex items-center gap-2 rounded-full text-white px-6 py-2.5 font-semibold transition-transform hover:scale-105" style={{ background: accent }}>
                    <MinusCircle size={16} /> Log time
                  </button>
                </div>
              </div>
            )}

            {data.redemptions.length > 0 && (
              <>
                <h2 className="font-head text-lg font-semibold text-[#3D405B] mb-4">Recently used</h2>
                <div className="space-y-2">
                  {data.redemptions.map((r) => (
                    <div key={r.id} data-testid={`redemption-${r.id}`} className="flex items-center justify-between rounded-xl bg-white border border-[#EAE6DF] px-4 py-3 text-sm">
                      <span className="text-[#3D405B]">{r.note || "Screen time used"}</span>
                      <span className="flex items-center gap-3">
                        <span className="text-[#E07A5F] font-semibold">−{fmt(r.minutes)}</span>
                        <span className="text-xs text-[#6B7280]">{new Date(r.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}
